import { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import {
  ChartLineUp,
  Handshake,
  Scales,
  TrendUp,
  Trophy,
  XCircle,
  Coins,
  Stack,
  Percent,
} from '@phosphor-icons/react';
import { formatCurrency } from 'shared/utils/currency';
import { AcquisitionStatsOverview } from '../types/statistics.types';
import { MetricInfo } from './MetricInfo';

interface KpiCardsProps {
  overview?: AcquisitionStatsOverview;
  loading: boolean;
}

const cardClass = 'bg-white';

interface Kpi {
  title: string;
  value: string;
  sub: string;
  icon: ReactNode;
  info: ReactNode;
}

export const KpiCards = ({ overview, loading }: KpiCardsProps) => {
  if (loading || !overview) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => (
          <Card key={i} className={cardClass}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-3 w-32 mt-2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const winRatePct = (overview.winRate * 100).toFixed(0);
  const avgYieldPct = (overview.avgDealYield * 100).toFixed(2);
  const weightedYieldPct = (overview.volumeWeightedDealYield * 100).toFixed(2);
  const decided = overview.deals + overview.rejected;

  const kpis: Kpi[] = [
    {
      title: 'Totale dossiers',
      value: overview.total.toLocaleString('nl-BE'),
      sub: `${overview.open} actief in pipeline`,
      icon: <Stack weight="duotone" className="h-5 w-5 text-moss-primary" />,
      info: (
        <p>
          Aantal niet-gearchiveerde acquisitiedossiers, ongeacht fase. Inclusief
          afgewezen en gewonnen deals.
        </p>
      ),
    },
    {
      title: 'Gewonnen deals',
      value: overview.deals.toLocaleString('nl-BE'),
      sub: `${overview.rejected} afgewezen`,
      icon: <Handshake weight="duotone" className="h-5 w-5 text-moss-primary" />,
      info: (
        <p>
          Aantal dossiers in fase <strong>"Deal!"</strong>. Subtitel toont aantal
          dossiers in fase <strong>"Afgewezen"</strong>.
        </p>
      ),
    },
    {
      title: 'Win-rate',
      value: `${winRatePct}%`,
      sub: `${overview.deals} van ${decided} afgehandelde dossiers`,
      icon: <Trophy weight="duotone" className="h-5 w-5 text-moss-primary" />,
      info: (
        <div className="space-y-1">
          <p>
            Welk aandeel van de <em>afgehandelde</em> dossiers werd een deal.
          </p>
          <p>
            <strong>Formule:</strong> deals / (deals + afgewezen)
          </p>
          <p>
            <strong>Telling vandaag:</strong> {overview.deals} / ({overview.deals} +{' '}
            {overview.rejected}) = {winRatePct}%
          </p>
          <p>
            Open dossiers (Inbox / Opgepikt / Agenda ACQ / Agenda RVB) tellen{' '}
            <em>niet</em> mee.
          </p>
        </div>
      ),
    },
    {
      title: 'Gem. yield op deals',
      value: `${avgYieldPct}%`,
      sub: 'Rekenkundig gemiddelde',
      icon: <Percent weight="duotone" className="h-5 w-5 text-moss-primary" />,
      info: (
        <div className="space-y-1">
          <p>
            Gemiddeld netto-rendement (<code>bidPriceActualNetYield</code>) over
            alle gewonnen deals.
          </p>
          <p>
            <strong>Formule:</strong> Σ(yield<sub>i</sub>) / aantal deals met
            ingevulde yield
          </p>
        </div>
      ),
    },
    {
      title: 'Volume-gewogen yield (deals)',
      value: `${weightedYieldPct}%`,
      sub: 'Grote transacties wegen zwaarder',
      icon: <Scales weight="duotone" className="h-5 w-5 text-moss-primary" />,
      info: (
        <div className="space-y-1">
          <p>
            Het meest representatieve rendement op het transactievolume. Een deal
            van €10M met 6% telt 10× zwaarder dan een deal van €1M met 6%.
          </p>
          <p>
            <strong>Formule:</strong> Σ(yield<sub>i</sub> × bod
            <sub>i</sub>) / Σ(bod<sub>i</sub>) over deals
          </p>
        </div>
      ),
    },
    {
      title: 'Transactievolume gewonnen',
      value: formatCurrency(overview.totalDealVolume),
      sub: 'Som biedprijzen op gewonnen deals',
      icon: <TrendUp weight="duotone" className="h-5 w-5 text-brick-primary" />,
      info: (
        <div className="space-y-1">
          <p>Totale transactiegrootte van gewonnen deals.</p>
          <p>
            <strong>Formule:</strong> Σ <code>(totalBid || bidPriceActual)</code>{' '}
            over dossiers in fase "Deal!"
          </p>
        </div>
      ),
    },
    {
      title: 'Pipeline volume',
      value: formatCurrency(overview.totalPipelineVolume),
      sub: 'Open + gesloten dossiers',
      icon: <ChartLineUp weight="duotone" className="h-5 w-5 text-brick-primary" />,
      info: (
        <div className="space-y-1">
          <p>
            Indicatieve volume-pijplijn — exclusief afgewezen dossiers, inclusief
            gewonnen deals.
          </p>
          <p>
            <strong>Formule:</strong> Σ{' '}
            <code>(totalBid || bidPriceActual || grossAskingPrice)</code> over
            niet-afgewezen dossiers.
          </p>
          <p>
            Voor open dossiers wordt de vraagprijs gebruikt als geen biedprijs
            bekend is.
          </p>
        </div>
      ),
    },
    {
      title: 'In pipeline',
      value: overview.open.toLocaleString('nl-BE'),
      sub: 'Inbox / Opgepikt / Agenda ACQ / Agenda RVB',
      icon: <Coins weight="duotone" className="h-5 w-5 text-neutral-secondary-mid" />,
      info: (
        <p>
          Aantal dossiers dat nog in behandeling is (niet gesloten en niet
          afgewezen).
        </p>
      ),
    },
    {
      title: 'Afgewezen',
      value: overview.rejected.toLocaleString('nl-BE'),
      sub: 'Niet doorgegaan',
      icon: <XCircle weight="duotone" className="h-5 w-5 text-neutral-secondary-mid" />,
      info: <p>Aantal dossiers in fase "Afgewezen".</p>,
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {kpis.map((kpi) => (
        <Card key={kpi.title} className={cardClass}>
          <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-1.5">
              <CardTitle className="text-sm font-medium text-neutral-secondary-mid">
                {kpi.title}
              </CardTitle>
              <MetricInfo description={kpi.info} />
            </div>
            {kpi.icon}
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold text-neutral-primary">
              {kpi.value}
            </div>
            <p className="text-xs text-neutral-secondary-mid mt-1">{kpi.sub}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
